import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Thin animated bar across the very top of the viewport. Visible while
 * heavy route transitions are settling — it gives users the same "something
 * is happening" affordance browsers used to provide natively.
 *
 * Implementation: on every `pathname` change we flip the bar to active for
 * up to ~600ms, then off. We don't try to track real load progress; the bar
 * is purely a perceived-performance affordance (same pattern as NProgress,
 * Vercel's nav bar, GitHub's PJAX bar).
 */
export default function RouteProgressBar() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const firstRender = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => {
    // Skip the very first render — there's no transition happening on mount.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setActive(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActive(false), 600);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location.pathname]);

  return <div className={`nprogress-bar ${active ? 'active' : ''}`} aria-hidden="true" />;
}
